import {
  Component,
  OnChanges,
  Directive,
  Input,
  Output,
  ViewContainerRef,
  ElementRef,
  EventEmitter,
  OnInit,
  ViewChild,
  ComponentFactoryResolver, HostListener
} from '@angular/core';
import { ColorPickerService } from './color-picker.service';
import { Rgba, Hsla, Hsva, SliderPosition, SliderDimension } from './classes';
import { NgModule, ReflectiveInjector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AppStateService } from '../services/app-state.service';

// An internal rect structure to ease some calculations
interface IRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
}


@Directive({
  selector: '[colorPicker]'
})
export class ColorPickerDirective implements OnInit, OnChanges {
  @Input() colorPicker: string;
  @Output() colorPickerChange = new EventEmitter<string>(true);
  @Input() cpToggle: boolean;
  @Output() cpToggleChange = new EventEmitter<boolean>(true);
  @Input() cpPosition: string = 'right';
  @Input() cpPositionOffset: string = '0%';
  @Input() cpPositionRelativeToArrow: boolean = false;
  @Input() cpOutputFormat: string = 'hex';
  @Input() cpPresetLabel: string = 'Custom colors';
  @Input() cpPresetColors: Array<any>;
  @Input() cpCancelButton: boolean = false;
  @Input() cpCancelButtonClass: string = 'cp-cancel-button-class';
  @Input() cpCancelButtonText: string = 'Cancel';
  @Input() cpOKButton: boolean = false;
  @Input() cpOKButtonClass: string = 'cp-ok-button-class';
  @Input() cpOKButtonText: string = 'OK';
  @Input() cpFallbackColor: string = '#00000';
  @Input() cpHeight: string = 'auto';
  @Input() cpWidth: string = '230px';
  @Input() cpIgnoredElements: any = [];
  @Input() cpDialogDisplay: string = 'popup';
  @Input() cpSaveClickOutside: boolean = true;
  @Input() cpAlphaChannel: string = 'hex6';
  @Input() containerSelector: string;
  @Output() cpPresetColorsChanged = new EventEmitter<Array<any>>(true);

  private dialog: any;
  private created: boolean;
  private ignoreChanges: boolean = false;


  constructor(private componentFactoryResolver: ComponentFactoryResolver,
    private vcRef: ViewContainerRef,
    private el: ElementRef,
    private service: ColorPickerService) {
    this.created = false;

  }

  ngOnChanges(changes: any): void {
    if (changes.cpToggle) {
      if (changes.cpToggle.currentValue) {
        this.openDialog();
      }

      if (!changes.cpToggle.currentValue && this.dialog) {
        this.dialog.closeColorPicker();
      }
    }
    if (changes.colorPicker) {
      if (this.dialog && !this.ignoreChanges) {
        if (this.cpDialogDisplay === 'inline') {
          this.dialog.setInitialColor(changes.colorPicker.currentValue);
        }
        this.dialog.setColorFromString(changes.colorPicker.currentValue, false);
      }
      this.ignoreChanges = false;
    }
  }

  ngOnInit() {
    if (this.colorPicker === null || this.colorPicker === '') {
      if (this.colorPicker === null) {
        this.colorPickerChange.emit(null);
      } else {
        this.colorPickerChange.emit('');
      }
    } else {
      let hsva = this.service.stringToHsva(this.colorPicker);
      if (hsva === null) {
        hsva = this.service.stringToHsva(this.colorPicker, true);
      }
      if (hsva === null) {
        hsva = this.service.stringToHsva(this.cpFallbackColor);
      }
      this.colorPickerChange.emit(this.service.outputFormat(hsva, this.cpOutputFormat, this.cpAlphaChannel === 'hex8'));
    }
  }

  @HostListener('click')
  onClick() {
    if (this.cpIgnoredElements.filter((item: any) => item === this.el.nativeElement).length === 0) {
      this.openDialog();
    }
  }

  openDialog() {
    if (!this.created) {
      this.created = true;
      const factory = this.componentFactoryResolver.resolveComponentFactory(DialogComponent);
      const injector = ReflectiveInjector.fromResolvedProviders([], this.vcRef.parentInjector);
      const cmpRef = this.vcRef.createComponent(factory, 0, injector, []);

      cmpRef.instance.setDialog(this, this.el, this.colorPicker, this.cpPosition, this.cpPositionOffset,
        this.cpPositionRelativeToArrow, this.cpOutputFormat, this.cpPresetLabel, this.cpPresetColors,
        this.cpCancelButton, this.cpCancelButtonClass, this.cpCancelButtonText,
        this.cpOKButton, this.cpOKButtonClass, this.cpOKButtonText, this.cpHeight, this.cpWidth,
        this.cpIgnoredElements, this.cpDialogDisplay, this.cpSaveClickOutside, this.cpAlphaChannel,
        this.containerSelector);
      this.dialog = cmpRef.instance;
      // this.viewContainerRef.createComponent(factory);
      //  this.compiler.compileModuleAndAllComponentsAsync(DynamicCpModule)
      //       .then(factory => {
      //            const compFactory = factory.componentFactories.find(x => x.componentType === DialogContainerComponent);
      //           const injector = ReflectiveInjector.fromResolvedProviders([], this.vcRef.parentInjector);
      //            const cmpRef = this.vcRef.createComponent(compFactory, 0, injector, []);
      //           cmpRef.instance.setDialog(this, this.el, this.colorPicker, this.cpPosition, this.cpPositionOffset,
      //               this.cpPositionRelativeToArrow, this.cpOutputFormat, this.cpPresetLabel, this.cpPresetColors,
      //               this.cpCancelButton, this.cpCancelButtonClass, this.cpCancelButtonText,
      //             this.cpOKButton, this.cpOKButtonClass, this.cpOKButtonText, this.cpHeight, this.cpWidth,
      //               this.cpIgnoredElements, this.cpDialogDisplay, this.cpSaveClickOutside, this.cpAlphaChannel);
      //          this.dialog = cmpRef.instance;
      //       });
    } else if (this.dialog) {
      this.dialog.openDialog(this.colorPicker);
    }
  }

  closeDialog() {
    if (this.dialog) {
      this.dialog.closeColorPicker();
    }
  }

  colorChanged(value: string, ignore: boolean = true) {
    this.ignoreChanges = ignore;
    this.colorPickerChange.emit(value);

  }

  @HostListener('input', ['$event'])
  changeInput(value: string) {
    if (this.dialog) {
      this.dialog.setColorFromString(value, true);
    }
    // else {
    // this.compiler.compileModuleAndAllComponentsAsync(DynamicCpModule)
    //   .then(factory => {
    //     const compFactory = factory.componentFactories.find(x => x.componentType === DialogContainerComponent);
    //     const injector = ReflectiveInjector.fromResolvedProviders([], this.vcRef.parentInjector);
    //     const cmpRef = this.vcRef.createComponent(compFactory, 0, injector, []);
    //     cmpRef.instance.setDialog(this, this.el, this.colorPicker, this.cpPosition, this.cpPositionOffset,
    //        this.cpPositionRelativeToArrow, this.cpOutputFormat, this.cpPresetLabel, this.cpPresetColors,
    //       this.cpCancelButton, this.cpCancelButtonClass, this.cpCancelButtonText,
    //       this.cpOKButton, this.cpOKButtonClass, this.cpOKButtonText, this.cpHeight, this.cpWidth,
    //       this.cpIgnoredElements, this.cpDialogDisplay, this.cpSaveClickOutside, this.cpAlphaChannel);
    //     this.dialog = cmpRef.instance;
    //    this.dialog.setColorFromString(value, true);
    //  });
    // }
  }

  toggle(value: boolean) {
    this.cpToggleChange.emit(value);
  }

  presetColorsChanged(value: Array<any>) {
    this.cpPresetColorsChanged.emit(value);
  }

}

@Directive({
  selector: '[text]'
})
export class TextDirective {
  @Output() newValue = new EventEmitter<any>();
  @Input() text?: any;
  @Input() rg?: number;

  @HostListener('input', ['$event'])
  changeInput(keyboardEvent: KeyboardEvent) {
    if (this.rg === undefined) {
      this.newValue.emit(keyboardEvent);
    } else {
      const numeric = parseFloat(keyboardEvent.target['value']);
      if (!isNaN(numeric) && numeric >= 0 && numeric <= this.rg) {
        this.newValue.emit({ v: numeric, rg: this.rg });
      }
    }
  }
}

@Directive({
  selector: '[slider]'
})
export class SliderDirective {
  @Output() newValue = new EventEmitter<any>();
  @Input() slider?: string;
  @Input() rgX: number;
  @Input() rgY: number;
  private readonly listenerMove: any;
  private readonly listenerStop: any;

  constructor(private el: ElementRef) {
    this.listenerMove = (event: any) => {
      this.move(event);
    };
    this.listenerStop = () => {
      this.stop();
    };
  }

  setCursor(event: any) {
    const height = this.el.nativeElement.offsetHeight;
    const width = this.el.nativeElement.offsetWidth;
    const x = Math.max(0, Math.min(this.getX(event), width));
    const y = Math.max(0, Math.min(this.getY(event), height));

    if (this.rgX !== undefined && this.rgY !== undefined) {
      this.newValue.emit({ s: x / width, v: (1 - y / height), rgX: this.rgX, rgY: this.rgY });
    } else if (this.rgX === undefined && this.rgY !== undefined) { // Ready to use vertical sliders
      this.newValue.emit({ v: y / height, rg: this.rgY });
    } else {
      this.newValue.emit({ v: x / width, rg: this.rgX });
    }
  }

  move(event: any) {
    event.preventDefault();
    this.setCursor(event);
  }

  @HostListener('mousedown', ['$event'])
  @HostListener('touchstart', ['$event'])
  start(event: any) {
    this.setCursor(event);
    document.addEventListener('mousemove', this.listenerMove);
    document.addEventListener('touchmove', this.listenerMove);
    document.addEventListener('mouseup', this.listenerStop);
    document.addEventListener('touchend', this.listenerStop);
  }

  stop() {
    document.removeEventListener('mousemove', this.listenerMove);
    document.removeEventListener('touchmove', this.listenerMove);
    document.removeEventListener('mouseup', this.listenerStop);
    document.removeEventListener('touchend', this.listenerStop);
  }

  getX(event: any): number {
    return (
      (event.pageX !== undefined ? event.pageX : event.touches[0].pageX) -
      this.el.nativeElement.getBoundingClientRect().left - window.pageXOffset
    );
  }

  getY(event: any): number {
    return (
      (event.pageY !== undefined ? event.pageY : event.touches[0].pageY) -
      this.el.nativeElement.getBoundingClientRect().top - window.pageYOffset
    );
  }
}

@Component({
  selector: 'color-picker',
  templateUrl: 'color-picker.html'
})

export class DialogComponent implements OnInit {
  hsva: Hsva;
  rgbaText: Rgba;
  hslaText: Hsla;
  hexText: string;
  outputColor: string;
  selectedColor: string;
  alphaSliderColor: string;
  hueSliderColor: string = 'rgb(255,0,0)';
  slider: SliderPosition;
  sliderDimMax: SliderDimension;
  format: number;
  show: boolean;
  top: number;
  left: number;
  position: string;
  directiveInstance: any;
  initialColor: string;
  directiveElementRef: ElementRef;

  listenerMouseDown: any;
  listenerResize: any;

  cpPosition: string;
  cpPositionOffset: number;
  cpOutputFormat: string;
  cpPresetLabel: string;
  cpPresetColors: Array<any>;
  selectedColorIndex: any = null;
  cpCancelButton: boolean;
  cpCancelButtonClass: string;
  cpCancelButtonText: string;
  cpOKButton: boolean;
  cpOKButtonClass: string;
  cpOKButtonText: string;
  cpHeight: number;
  cpWidth: number;
  cpIgnoredElements: any;
  cpDialogDisplay: string;
  cpSaveClickOutside: boolean;
  cpAlphaChannel: string;

  dialogArrowSize: number = 10;
  dialogArrowOffset: number = 15;
  arrowTop: number;
  arrowLeft: number;

  classMap: any;
  containerSelector: string;

  @ViewChild('hueSlider', {static: true}) hueSlider: any;
  @ViewChild('alphaSlider', {static: true}) alphaSlider: any;

  @ViewChild('dialogPopup', {static: false}) dialogElement: any;

  constructor(private el: ElementRef, private service: ColorPickerService, private appStateService: AppStateService) {
    this.classMap = { 'no-color-selected': false };
  }

  setDialog(instance: any, elementRef: ElementRef, color: any, cpPosition: string, cpPositionOffset: string,
    cpPositionRelativeToArrow: boolean, cpOutputFormat: string, cpPresetLabel: string, cpPresetColors: Array<any>,
    cpCancelButton: boolean, cpCancelButtonClass: string, cpCancelButtonText: string,
    cpOKButton: boolean, cpOKButtonClass: string, cpOKButtonText: string,
    cpHeight: string, cpWidth: string,
    cpIgnoredElements: any, cpDialogDisplay: string, cpSaveClickOutside: boolean, cpAlphaChannel: string,
    containerSelector: string) {
    this.directiveInstance = instance;
    this.initialColor = color;
    this.directiveElementRef = elementRef;
    this.cpPosition = cpPosition;
    this.cpPositionOffset = parseInt(cpPositionOffset);
    if (!cpPositionRelativeToArrow) {
      this.dialogArrowOffset = 0;
    }

    this.cpOutputFormat = cpOutputFormat;
    this.cpPresetLabel = cpPresetLabel;
    this.cpPresetColors = cpPresetColors;

    this.cpCancelButton = cpCancelButton;
    this.cpCancelButtonClass = cpCancelButtonClass;
    this.cpCancelButtonText = cpCancelButtonText;
    this.cpOKButton = cpOKButton;
    this.cpOKButtonClass = cpOKButtonClass;
    this.cpOKButtonText = cpOKButtonText;
    this.cpHeight = parseInt(cpHeight);
    this.cpWidth = parseInt(cpWidth);
    this.cpIgnoredElements = cpIgnoredElements;
    this.cpDialogDisplay = cpDialogDisplay;
    if (this.cpDialogDisplay === 'inline') {
      this.dialogArrowOffset = 0;
      this.dialogArrowSize = 0;
    }
    this.cpSaveClickOutside = cpSaveClickOutside;
    this.cpAlphaChannel = cpAlphaChannel;
    this.containerSelector = containerSelector;
  }

  ngOnInit() {
    const alphaWidth = this.alphaSlider.nativeElement.offsetWidth;
    const hueWidth = this.hueSlider.nativeElement.offsetWidth;
    this.sliderDimMax = new SliderDimension(hueWidth, this.cpWidth, 130, alphaWidth);
    this.slider = new SliderPosition(0, 0, 0, 0);
    if (this.cpOutputFormat === 'rgba') {
      this.format = 1;
      // }
      // else if (this.cpOutputFormat === 'hsla') {
      //    this.format = 2;
    } else {
      this.format = 0;
    }
    this.listenerMouseDown = (event: any) => {
      this.onMouseDown(event);
    };
    this.listenerResize = () => {
      this.onResize();
    };
    this.openDialog(this.initialColor, false);
  }

  get ColorText() {
    return this.getHexText();
  }

  set ColorText(value: string) {
    this.setColorFromString(value);
  }

  setInitialColor(color: any) {
    this.initialColor = color;
  }

  openDialog(color: any, emit: boolean = true) {

    if (color === '' || color === null) {
      this.removeColor();
    } else {
      this.setInitialColor(color);
      this.setColorFromString(color, emit);
    }
    this.openColorPicker();
  }

  cancelColor(event: any) {
    event.stopPropagation();
    this.setColorFromString(this.initialColor, true);
    if (this.cpDialogDisplay === 'popup') {
      this.directiveInstance.colorChanged(this.initialColor, true);
      this.closeColorPicker();
    }
  }

  oKColor(event: any) {
    event.stopPropagation();
    if (this.cpDialogDisplay === 'popup') {
      this.closeColorPicker();
    }
  }


  setColorFromString(value: string, emit: boolean = true) {
    if (value === null || value === '') {
      // we set the default value to be with saturation and value to 100% so when the user selects a color, it will show
      // as a full saturated color and not black or white
      value = 'hsv(0,100%,100%)';
    }

    // trim the text
    value = value.trim();

    let hsva: Hsva;
    if (this.cpAlphaChannel === 'hex8') {
      hsva = this.service.stringToHsva(value, true);
      if (!hsva && !this.hsva) {
        hsva = this.service.stringToHsva(value, false);
      }
    } else {
      hsva = this.service.stringToHsva(value, false);
    }
    if (hsva) {
      this.hsva = hsva;
      this.update(emit);
    }
  }

  onMouseDown(event: any) {
    if ((!this.isDescendant(this.el.nativeElement, event.target)
      && event.target !== this.directiveElementRef.nativeElement &&
      this.cpIgnoredElements.filter((item: any) => item === event.target).length === 0) && this.cpDialogDisplay === 'popup') {
      if (!this.cpSaveClickOutside) {
        this.setColorFromString(this.initialColor, false);
        this.directiveInstance.colorChanged(this.initialColor);
      }
      this.closeColorPicker();
    }
  }

  selectInputShowCursorInAllBrowsers(event: any) {
    if (this.appStateService.isBrowserEdge() || this.appStateService.isBrowserIE()) {
      const target: HTMLInputElement = event.target || event.srcElement || event.currentTarget;
      target.select();
    }
  }

  openColorPicker() {
    if (!this.show) {
      this.setDialogPosition();
      this.show = true;
      this.directiveInstance.toggle(true);
      document.addEventListener('mousedown', this.listenerMouseDown);
      window.addEventListener('resize', this.listenerResize);
      this.classMap['no-color-selected'] = (this.initialColor === null || this.initialColor === '') ? true : false;
    }
  }

  closeColorPicker() {
    if (this.show) {
      this.show = false;
      this.directiveInstance.toggle(false);
      document.removeEventListener('mousedown', this.listenerMouseDown);
      window.removeEventListener('resize', this.listenerResize);
    }
  }

  onResize() {
    if (this.cpDialogDisplay !== 'inline') {
      this.closeColorPicker();
    }
  }

  setDialogPosition() {
    let scrollingContainerBoundingRect: ClientRect | IRect = null;
    let scrollingContainerElement: Element;

    if (this.containerSelector) {
      scrollingContainerElement = document.querySelector(this.containerSelector);
      scrollingContainerBoundingRect = scrollingContainerElement.getBoundingClientRect();
    } else {
      // make the scrolling container the whole document
      scrollingContainerBoundingRect = {
        top: 0,
        left: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
      };
    }

    const buttonElement = this.directiveElementRef.nativeElement;
    const buttonRect = buttonElement.getBoundingClientRect();

    const position = 'fixed';
    this.cpWidth = 230;
    this.cpHeight = 410;
    const arrowWidth: number = 10;
    const arrowHeight: number = 10;

    const dialogWidth = this.cpWidth;
    const dialogHeight = this.cpHeight;

    const arrowContainerHeight: number = 20;
    this.position = 'fixed';

    const isColorPickerAtTheBottomOfBoundedElement = () => (buttonRect.bottom + (dialogHeight) < scrollingContainerBoundingRect.bottom);
    const isColorPickerAtTheTopOfBoundedElement = () => (buttonRect.top - (dialogHeight) > scrollingContainerBoundingRect.top);
    const isColorPickerAtTheLeftOfBoundedElement = () => (buttonRect.left - (dialogWidth) > scrollingContainerBoundingRect.left);
    const isColorPickerAtTheRightOfBoundedElement = () => (buttonRect.right + (dialogWidth) < scrollingContainerBoundingRect.right);

    if (isColorPickerAtTheBottomOfBoundedElement()) {
      this.cpPosition = 'bottom';
      this.arrowTop = buttonRect.top + buttonRect.height - arrowHeight;
      this.top = this.arrowTop + arrowContainerHeight;
      this.left = buttonRect.left + buttonRect.width / 2 - dialogWidth / 2;
      this.arrowLeft = buttonRect.left + buttonRect.width / 2 - arrowWidth / 2;
    } else if (isColorPickerAtTheTopOfBoundedElement()) {
      this.cpPosition = 'top';
      this.arrowTop = buttonRect.top - arrowHeight;
      this.top = this.arrowTop - dialogHeight;
      this.left = buttonRect.left + buttonRect.width / 2 - dialogWidth / 2;
      this.arrowLeft = buttonRect.left + buttonRect.width / 2 - arrowWidth / 2;
    } else if (isColorPickerAtTheLeftOfBoundedElement()) {
      this.cpPosition = 'left';
      this.arrowTop = buttonRect.top + buttonRect.height / 2 - arrowHeight / 2;
      this.top = buttonRect.top + buttonRect.height / 2 - dialogHeight / 2;

      this.arrowLeft = buttonRect.left - arrowWidth;
      this.left = this.arrowLeft - dialogWidth;
    } else if (isColorPickerAtTheRightOfBoundedElement()) {
      this.cpPosition = 'right';
      this.arrowTop = buttonRect.top + buttonRect.height / 2 - arrowHeight / 2;
      this.top = buttonRect.top + buttonRect.height / 2 - dialogHeight / 2;

      this.arrowLeft = buttonRect.left + buttonRect.width - arrowWidth;
      this.left = this.arrowLeft + arrowWidth * 2;
    } else {
      this.cpPosition = 'bottom';
      this.arrowTop = buttonRect.top + buttonRect.height - arrowHeight;
      this.top = this.arrowTop + arrowContainerHeight;
      this.left = buttonRect.left + buttonRect.width / 2 - dialogWidth / 2;
      this.arrowLeft = buttonRect.left + buttonRect.width / 2 - arrowWidth / 2;
    }
  }

  setSaturation(val: { v: number, rg: number }) {
    const hsla = this.service.hsva2hsla(this.hsva);
    hsla.s = val.v / val.rg;
    this.hsva = this.service.hsla2hsva(hsla);
    this.update();
  }

  setLightness(val: { v: number, rg: number }) {
    const hsla = this.service.hsva2hsla(this.hsva);
    hsla.l = val.v / val.rg;
    this.hsva = this.service.hsla2hsva(hsla);
    this.update();
  }

  setHue(val: { v: number, rg: number }) {
    if (this.hsva === null || typeof (this.hsva) === 'undefined') {
      // we set the default value to be with saturation and value to 100% so when the user selects a color, it will show
      // as a full saturated color and not black or white
      this.hsva = new Hsva(0, 1, 1, 0);
      this.hsva.h = val.v / val.rg;
      this.update();
    } else {
      this.hsva.h = val.v / val.rg;
      this.update();
    }
  }

  setAlpha(val: { v: number, rg: number }) {
    this.hsva.a = val.v / val.rg;
    this.update();
  }

  setR(val: { v: number, rg: number }) {
    const rgba = this.service.hsvaToRgba(this.hsva);
    rgba.r = val.v / val.rg;
    this.hsva = this.service.rgbaToHsva(rgba);
    this.update();
  }

  setG(val: { v: number, rg: number }) {
    const rgba = this.service.hsvaToRgba(this.hsva);
    rgba.g = val.v / val.rg;
    this.hsva = this.service.rgbaToHsva(rgba);
    this.update();
  }

  setB(val: { v: number, rg: number }) {
    const rgba = this.service.hsvaToRgba(this.hsva);
    rgba.b = val.v / val.rg;
    this.hsva = this.service.rgbaToHsva(rgba);
    this.update();
  }

  setSaturationAndBrightness(val: { s: number, v: number, rgX: number, rgY: number }) {
    if (typeof (val) !== 'undefined') {
      if (typeof (this.hsva) === 'undefined') {
        this.hsva = new Hsva(null, null, null, null);
      }
      this.hsva.s = val.s / val.rgX;
      this.hsva.v = val.v / val.rgY;
      this.update();
    }
  }

  formatPolicy(event: any): number {
    event.stopPropagation();
    if (this.format === 0) {
      this.format = 1;
    } else {
      this.format = 0;
    }
    return this.format;
  }

  update(emit: boolean = true) {
    this.classMap['no-color-selected'] = this.hsva === null;
    if (this.hsva === null) {
      // we set the default value to be with saturation and value to 100% so when the user selects a color, it will show
      // as a full saturated color and not black or white
      this.hsva = new Hsva(0, 1, 1, 0);
    }
    const hsla = this.service.hsva2hsla(this.hsva);
    const rgba = this.service.denormalizeRGBA(this.service.hsvaToRgba(this.hsva));
    const hueRgba = this.service.denormalizeRGBA(this.service.hsvaToRgba(new Hsva(this.hsva.h, 1, 1, 1)));

    this.hslaText = new Hsla(Math.round((hsla.h) * 360), Math.round(hsla.s * 100),
      Math.round(hsla.l * 100), Math.round(hsla.a * 100) / 100);

    this.rgbaText = new Rgba(rgba.r, rgba.g, rgba.b, Math.round(rgba.a * 100) / 100);
    this.hexText = this.service.hexText(rgba, this.cpAlphaChannel === 'hex8');

    this.alphaSliderColor = 'rgb(' + rgba.r + ',' + rgba.g + ',' + rgba.b + ')';
    this.hueSliderColor = 'rgb(' + hueRgba.r + ',' + hueRgba.g + ',' + hueRgba.b + ')';

    if (this.format === 0 && this.hsva.a < 1 && this.cpAlphaChannel === 'hex6') {
      this.format++;
    }

    const lastOutput = this.outputColor;
    this.outputColor = this.service.outputFormat(this.hsva, this.cpOutputFormat, this.cpAlphaChannel === 'hex8');
    this.selectedColor = this.outputColor; // this.service.outputFormat(this.hsva, 'rgba', false);

    if (!this.sliderDimMax) {
      const alphaWidth = this.alphaSlider.nativeElement.offsetWidth;
      const hueWidth = this.hueSlider.nativeElement.offsetWidth;
      this.sliderDimMax = new SliderDimension(hueWidth, this.cpWidth, 130, alphaWidth);
    }

    this.slider = new SliderPosition((this.hsva.h) * this.sliderDimMax.h - 8, this.hsva.s * this.sliderDimMax.s - 8,
      (1 - this.hsva.v) * this.sliderDimMax.v - 8, this.hsva.a * this.sliderDimMax.a - 8);

    if (emit) { // && lastOutput !== this.outputColor) {
      this.directiveInstance.colorChanged(this.outputColor);
    }
  }

  isDescendant(parent: any, child: any): boolean {
    let node: any = child.parentNode;
    while (node !== null) {
      if (node === parent) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  }

  createBox(element: any, offset: boolean): any {
    return {
      top: element.getBoundingClientRect().top + (offset ? window.pageYOffset : 0),
      left: element.getBoundingClientRect().left + (offset ? window.pageXOffset : 0),
      width: element.offsetWidth,
      height: element.offsetHeight
    };
  }

  getHexText(): any {
    return this.hexText ? this.hexText : '';
  }

  // Customize methods
  setColorFromSavedPalett(event: any, index: any, value: string) {
    event.stopPropagation();
    this.selectedColorIndex = index;
    this.setColorFromString(value);
  }

  removeColor(event: any = null) {
    if (event) {
      event.stopPropagation();
    }
    this.setInitialColor(null);
    this.setColorFromString(null);
    this.directiveInstance.colorChanged(this.initialColor, true);
    this.selectedColor = null;
    this.hexText = null;
    this.rgbaText = null;
    this.classMap['no-color-selected'] = true;
  }

  addToCustomColors(event: any) {
    event.stopPropagation();
    if (this.outputColor !== undefined && this.outputColor !== null) {
      if (this.selectedColorIndex !== null) {
        this.cpPresetColors[this.selectedColorIndex] = this.outputColor;
      } else {
        this.cpPresetColors[0] = this.outputColor;
      }
      this.directiveInstance.presetColorsChanged(this.cpPresetColors);
    } else {
      alert('No color selected for set to custom colors');
    }
  }

  getBorderColor(index: any) {
    if (this.selectedColorIndex === index) {
      return '#4c09ee';
    } else {
      return '#a9a9a9';
    }
  }
}

@NgModule({
  imports: [CommonModule, TranslateModule.forChild()],
  declarations: [DialogComponent, TextDirective, SliderDirective],
  exports: [DialogComponent],
  entryComponents: [DialogComponent]
})
export class DynamicCpModule {
}

